import { useEffect, useMemo, useState } from "react"
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Input,
  Modal,
  Row,
  Space,
  Typography,
} from "antd"
import { CheckboxChangeEventTarget } from "antd/es/checkbox/Checkbox"
import { SearchOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { CurrencyIcon } from "@src/app/components/CurrencyIcon"
import { Currency } from "@src/app/CurrenciesCollection"
import css from "./index.module.css"

type Props = {
  currencies: Currency[]
  initialCheckedList?: string[] // добавлено
  onCancel: () => void
  open: boolean
  update: (list: string[]) => void
}

export const SelectCurrencyModal = ({
  currencies,
  initialCheckedList = [],
  onCancel,
  open,
  update,
}: Props) => {
  const [checkedList, setCheckedList] = useState<string[]>([])
  const [search, setSearch] = useState("")

  useEffect(() => {
    if (open) {
      setCheckedList(initialCheckedList)
    }
  }, [open, initialCheckedList])

  const searched = useMemo(() => {
    return currencies.filter(
      (item) =>
        item.symbol_alias.toLowerCase().includes(search.toLowerCase()) ||
        item.display_name.toLowerCase().includes(search.toLowerCase()),
    )
  }, [search, currencies])

  const onChange = (v: CheckboxChangeEventTarget) => {
    const isInclude = checkedList.includes(v.value)

    if (!isInclude) {
      setCheckedList((value) => [...value, v.value])
    } else {
      setCheckedList((value) => value.filter((item) => item !== v.value))
    }
  }

  const isAllSelected = useMemo(() => {
    return currencies.length === checkedList.length
  }, [checkedList, currencies])

  const handleSelectAll = () => {
    const all = currencies.map((item) => `${item.symbol}_${item.network}`)
    setCheckedList(all)
  }

  const handleClearAll = () => {
    setCheckedList([])
  }

  const handleConfirm = () => {
    update(checkedList)
    onCancel()
  }

  return (
    <Modal
      closable
      footer={null}
      onCancel={onCancel}
      open={open}
      title={<Typography.Title>Enabled crypto</Typography.Title>}
    >
      <Row
        align="middle"
        justify="space-between"
        style={{ marginBottom: 12, marginRight: 15, padding: "0 8px" }}
      >
        <Col span={12}>
          <Input.Search
            allowClear
            enterButton={
              <Button icon={<SearchOutlined />} size="small" type="primary" />
            }
            onSearch={(s) => setSearch(s)}
            placeholder={"Search"}
            size="small"
          />
        </Col>
        <Col span={3}>
          <Flex align="center" gap={4}>
            <Checkbox
              checked={isAllSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  handleSelectAll()
                } else {
                  handleClearAll()
                }
              }}
            >
              All
            </Checkbox>
          </Flex>
        </Col>
      </Row>

      <ul className={css.list}>
        {searched.map((item) => {
          const key = `${item.symbol}_${item.network}`
          return (
            <li
              className={classNames(css.item, {
                [`${css.active}`]: checkedList.includes(key),
              })}
              key={`${key}_${item.display_name}`}
            >
              <Row align="middle" justify="space-between">
                <Col className={css.left} span={12}>
                  <Space className={css.space} size="small">
                    <CurrencyIcon network={item.network} symbol={item.symbol} />
                    <div className={css.currencyWrapper}>
                      <span className={css.availableSymbol}>
                        {item.network === "fiat"
                          ? item.symbol
                          : item.symbol_alias || "Disabled"}
                        <span
                          style={{ paddingLeft: 6 }}
                        >{`${item.token_kind ? `(${item.network})` : ""}`}</span>
                      </span>
                      {item.network !== "fiat" && (
                        <span>{`${item.token_kind ? `${item.token_kind}` : ""}`}</span>
                      )}
                    </div>
                  </Space>
                </Col>

                <Col className={css.right} span={3}>
                  <Checkbox
                    checked={checkedList.includes(key)}
                    onChange={(v) => onChange(v.target)}
                    value={key}
                  />
                </Col>
              </Row>
            </li>
          )
        })}
      </ul>

      <Flex justify="center" style={{ marginTop: 20 }}>
        <Button onClick={handleConfirm} type="primary">
          Accept
        </Button>
      </Flex>
    </Modal>
  )
}
